import React, { useState } from "react";
import { Link } from "react-router-dom";
import { get, orderBy } from "lodash";
import { styled } from "@mui/material/styles";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Fade,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import BSInput from "../../../common/js/components/BSInput";
import constants from "./constants";
import { formatPhoneNumber } from "../utils/operations";
import { Sorty, Search } from "../../../common/js/icons";

const CrmContacts = (props) => {
    const {
        contacts,
        columnToSort,
        deleteContact,
        elementsSaving,
        filterQuery,
        handleFilter,
        handleSort,
        sortDirection,
    } = props;

    const [confirmDelete, setConfirmDelete] = useState({
        open: false,
        contactId: null,
        contactIndex: null,
    });

    const handleDeleteContact = () => {
        deleteContact(confirmDelete.contactId, confirmDelete.contactIndex);
        handleClose();
    };

    const handleOpen = (contactId, contactIndex) => {
        setConfirmDelete((prevConfirmDelete) => ({
            ...prevConfirmDelete,
            open: true,
            contactId: contactId,
            contactIndex: contactIndex,
        }));
    };

    const handleClose = () => {
        setConfirmDelete((prevConfirmDelete) => ({
            ...prevConfirmDelete,
            open: false,
            contactId: null,
            contactIndex: null,
        }));
    };

    const data = orderBy(
        filterQuery && contacts
            ? contacts.filter((x) => constants.filterByQuery(x, "contacts", filterQuery))
            : contacts,
        columnToSort,
        sortDirection
    );

    return (
        <PaperSpacing>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <FilterInput>
                    <BSInput
                        extraInputStyles={{ backgroundColor: "#fafafa", border: "none", marginTop: "10px" }}
                        value={filterQuery}
                        placeholder="Search contacts..."
                        startAdornment={<Search />}
                        startAdornmentStyles={{ color: "#777777" }}
                        onChange={(event) => {
                            handleFilter(event.target.value);
                        }}
                    />
                </FilterInput>
            </div>
            {elementsSaving.crmContacts ? (
                <LoadingPlaceholder>
                    <CircularProgress color="primary" />
                </LoadingPlaceholder>
            ) : (
                <React.Fragment>
                    <StyledDivider />
                    <Fade in={true}>
                        <Table>
                            <TableHead>
                                <TableRow key={`crmContact-headers`}>
                                    <TableCell style={{ width: "20%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center" }}
                                            onClick={() => handleSort("contactFirstName")}
                                        >
                                            <Sortable>Name</Sortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>

                                    <TableCell style={{ width: "35%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center" }}
                                            onClick={() => handleSort("contactCompanyDisplayName")}
                                        >
                                            <Sortable>Company</Sortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>

                                    <TableCell align="right" style={{ width: "20%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", position: "relative" }}
                                            onClick={() => handleSort("contactPhoneNumber")}
                                        >
                                            <Sortable>Phone Number</Sortable>
                                        </div>
                                    </TableCell>

                                    <TableCell align="right" style={{ width: "15%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", position: "relative" }}
                                            onClick={() => handleSort("contactEmailAddress")}
                                        >
                                            <Sortable>Email</Sortable>
                                        </div>
                                    </TableCell>

                                    <TableCell style={{ width: "10%", padding: 0 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            {contacts && contacts.length > 0 ? (
                                <TableBody>
                                    {data.map((contact, index) => (
                                        <TableRow
                                            key={`crmcontact-row-${index}`}
                                            selectable="false"
                                            className="tableRow"
                                        >
                                            <LeftColumn>
                                                <IdLink to={`/contacts/${get(contact, "contactKey", "")}`}>
                                                    <div>{`${get(contact, "contactFirstName", "")} ${get(
                                                        contact,
                                                        "contactLastName",
                                                        ""
                                                    )}`}</div>
                                                </IdLink>
                                            </LeftColumn>

                                            <TableCell style={{ padding: 0 }}>
                                                {get(contact, "contactCompanyDisplayName", "")}
                                            </TableCell>

                                            <TableCell style={{ padding: 0 }}>
                                                {formatPhoneNumber(get(contact, "contactPhoneNumber", ""))}
                                            </TableCell>

                                            <TableCell style={{ padding: 0 }}>
                                                {get(contact, "contactEmailAddress", "")}
                                            </TableCell>

                                            <TableCell align="right" style={{ padding: 0 }}>
                                                <ShrinkButton
                                                    id={`delete-crmcontact-${index}`}
                                                    onClick={() => handleOpen(get(contact, "contactKey"), index)}
                                                >
                                                    <Delete />
                                                </ShrinkButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            ) : (
                                <TableBody>
                                    <TableRow>
                                        <NoData colSpan={6}>
                                            <AlternateContent>No Contacts</AlternateContent>
                                        </NoData>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </Fade>
                </React.Fragment>
            )}
            <DialogBox
                open={confirmDelete.open}
                onClose={handleClose}
                maxWidth="xs"
                aria-labelledby="confirmation-dialog-crm-contact"
            >
                <DialogContent>Delete this contact?</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteContact} color="primary" variant="contained">
                        Ok
                    </Button>
                </DialogActions>
            </DialogBox>
        </PaperSpacing>
    );
};

const Sortable = styled("span")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const IdLink = styled(Link)({
    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
});

const LeftColumn = styled(TableCell)({
    paddingLeft: "0px",
    padding: 0,
});

const PaperSpacing = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const FilterInput = styled("div")({
    height: "70px",
    marginBottom: "0px", // Fixed typo from "maringBottom"
});

const ShrinkButton = styled(Button)({
    padding: 0,
    minWidth: 0,
    minHeight: 0,
    margin: "0 18px 0 0",
    color: "#9e9e9e",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledDivider = styled(Divider)({
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    margin: "0 -20px 0 -20px",
});

const LoadingPlaceholder = styled("div")({
    marginTop: "3em",
    marginBottom: "1.5em",
    textAlign: "center",
    width: "100%",
});

const DialogBox = styled(Dialog)({
    width: "20em",
});

const AlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "10em",
    marginBottom: "3em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const NoData = styled(TableCell)({
    borderBottom: "none",
});

export default CrmContacts;
